import React from 'react';

import {TRANSFORMATION_STEP_TYPES} from 'analysis-tool/AnalysisToolConstants';
import {isViewTerminalStepType} from 'analysis-tool/data/LineageStepTypes';
import isAnalysisReadOnly from 'analysis-tool/isAnalysisReadOnly';
import AddTransformStepDropdown from 'analysis-tool/ui/charting/AddTransformStepDropdown';
import {BLOCKED_BY_DOWNSTREAM_FLAG_ROWS_MESSAGE} from 'analysis-tool/ui/charting/AddTransformStepDropdown';
import {FIX_FAILED_TRANSFORMS_MESSAGE} from 'analysis-tool/ui/charting/AddTransformStepDropdown';
import TransformPill from 'analysis-tool/ui/charting/TransformPill';
import AdditionalEditorSection from 'analysis-tool/ui/components/AdditionalEditorSection';
import isArchived from 'archive/isArchived';
import {PipelineStepDataKind} from 'graphql-client/__generated/graphqlGlobalTypes';
import hasDownstreamFlagRowsStep from 'pipelines/hasDownstreamFlagRowsStep';
import {isStepSucceeded} from 'pipelines/PipelineStepStatusUtils';
import {PipelineGraphQL, PipelineStepRawData, PipelineViewGraphQL} from 'pipelines/PipelineTypes';
import {getOutputDataForView, resolveLineageStep} from 'pipelines/PipelineViewUtils';
import {getLegacyStepFromAnalysis} from 'pipelines/StepUtil';

interface EditViewTransformsProps {
  analysis: PipelineGraphQL;
  view: PipelineViewGraphQL;
  dataFrameSpec: PipelineStepRawData<PipelineStepDataKind.DATASET> | null;
}

export default function EditViewTransforms({
  analysis,
  view,
  dataFrameSpec,
}: EditViewTransformsProps): JSX.Element {
  const viewSteps = view.lineage.map(({step}) => getLegacyStepFromAnalysis(analysis, step));
  const allStepsSucceeded = viewSteps.every(
    (step, index) => isStepSucceeded(step.statusCode) || index === viewSteps.length - 1
  );
  const blockedByDownstream = hasDownstreamFlagRowsStep(analysis, viewSteps[viewSteps.length - 1]);
  const canAddNewSteps = allStepsSucceeded && !blockedByDownstream;
  const addNewStepsDisabledMessage =
    (!allStepsSucceeded ? FIX_FAILED_TRANSFORMS_MESSAGE : undefined) ??
    (blockedByDownstream ? BLOCKED_BY_DOWNSTREAM_FLAG_ROWS_MESSAGE : undefined);

  // getOutputDataForView works for top level, but for view level the last step in the view lineage is a terminal step,
  // so we need the input data frame instead.
  const endsInTerminalViewStep = isViewTerminalStepType(viewSteps[viewSteps.length - 1].definition.stepType);
  // might be undefined if step is running/blocked, failed, etc.
  const maybeInputDataFrameId =
    endsInTerminalViewStep && dataFrameSpec != null
      ? dataFrameSpec.id
      : getOutputDataForView(view, analysis.steps)?.id;

  const transformPills = view.lineage
    .filter(({step: lineageStep}) =>
      TRANSFORMATION_STEP_TYPES.includes(resolveLineageStep(analysis.steps, {step: lineageStep}).stepType)
    )
    .map(({step: lineageStep}) => (
      <TransformPill
        key={lineageStep.id}
        analysis={analysis}
        step={getLegacyStepFromAnalysis(analysis, lineageStep)}
      />
    ));

  return (
    <AdditionalEditorSection
      title="Filters & Transforms"
      addItemElement={
        maybeInputDataFrameId && !isAnalysisReadOnly(analysis) && !isArchived(analysis) ? (
          <AddTransformStepDropdown
            viewSteps={viewSteps}
            dataFrameSpec={dataFrameSpec}
            inputDataFrameId={maybeInputDataFrameId}
            analysis={analysis}
            canAddNewSteps={canAddNewSteps}
            addNewStepsDisabledMessage={addNewStepsDisabledMessage}
          />
        ) : (
          <></>
        )
      }
      pills={transformPills}
      showTopBorder={false}
    />
  );
}
